<template>
  <span :class="inline ? '' : 'd-block'">
    <slot />
    <i 
      :style="iconStyle" 
      :class="getClass"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip"
      ref="triggerElement"
    ></i>
    
    <Teleport to="body">
      <div
        v-if="isVisible"
        ref="tooltipElement"
        class="popper-tooltip"
        :class="tooltipClasses"
        :style="tooltipStyles"
      >
        <div class="popper-arrow" :style="arrowStyles"></div>
        <div class="popper-content" v-html="content"></div>
      </div>
    </Teleport>
  </span>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'

export default {
  name: 'PromptIcon',
  props: {
    inline: {
      type: Boolean,
      default: false
    },
    content: String,
    iconStyle: String,
    class: String,
    placement: {
      type: String,
      default: "top",
    },
  },
  computed: {
    getClass() {
      return this.class || "icon-info iconsize";
    }
  },
  setup(props) {
    const isVisible = ref(false)
    const triggerElement = ref(null)
    const tooltipElement = ref(null)
    const tooltipPosition = ref({ top: 0, left: 0 })
    const arrowPosition = ref({})

    const tooltipClasses = computed(() => {
      return [
        'popper-tooltip',
        `popper-${props.placement}`,
        { 'popper-visible': isVisible.value }
      ]
    })

    const tooltipStyles = computed(() => {
      return {
        position: 'fixed',
        top: `${tooltipPosition.value.top}px`,
        left: `${tooltipPosition.value.left}px`,
        zIndex: 9999
      }
    })

    const arrowStyles = computed(() => {
      return {
        ...arrowPosition.value
      }
    })

    const calculatePosition = () => {
      if (!triggerElement.value || !tooltipElement.value) return

      const triggerRect = triggerElement.value.getBoundingClientRect()
      const tooltipRect = tooltipElement.value.getBoundingClientRect()
      const viewportWidth = window.innerWidth
      const viewportHeight = window.innerHeight

      let top = 0
      let left = 0
      let arrowTop = ''
      let arrowLeft = ''
      let arrowTransform = ''

      switch (props.placement) {
        case 'top':
          top = triggerRect.top - tooltipRect.height - 8
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2)
          arrowTop = '100%'
          arrowLeft = '50%'
          arrowTransform = 'translateX(-50%)'
          break
        case 'bottom':
          top = triggerRect.bottom + 8
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2)
          arrowTop = '-6px'
          arrowLeft = '50%'
          arrowTransform = 'translateX(-50%) rotate(180deg)'
          break
        case 'left':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2)
          left = triggerRect.left - tooltipRect.width - 8
          arrowTop = '50%'
          arrowLeft = '100%'
          arrowTransform = 'translateY(-50%) rotate(90deg)'
          break
        case 'right':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2)
          left = triggerRect.right + 8
          arrowTop = '50%'
          arrowLeft = '-6px'
          arrowTransform = 'translateY(-50%) rotate(-90deg)'
          break
      }

      // Keep tooltip within viewport bounds
      if (left < 8) left = 8
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8
      }
      if (top < 8) top = 8
      if (top + tooltipRect.height > viewportHeight - 8) {
        top = viewportHeight - tooltipRect.height - 8
      }

      tooltipPosition.value = { top, left }
      arrowPosition.value = {
        top: arrowTop,
        left: arrowLeft,
        transform: arrowTransform
      }
    }

    const showTooltip = async () => {
      isVisible.value = true
      await nextTick()
      calculatePosition()
    }

    const hideTooltip = () => {
      isVisible.value = false
    }

    const handleScroll = () => {
      if (isVisible.value) {
        calculatePosition()
      }
    }

    const handleResize = () => {
      if (isVisible.value) {
        calculatePosition()
      }
    }

    onMounted(() => {
      window.addEventListener('scroll', handleScroll, true)
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    })

    return {
      isVisible,
      triggerElement,
      tooltipElement,
      tooltipClasses,
      tooltipStyles,
      arrowStyles,
      showTooltip,
      hideTooltip
    }
  }
}
</script>

<style scoped>
.popper-tooltip {
  background: #333;
  color: white;
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  max-width: 300px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  pointer-events: none;
}

.popper-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border: 6px solid transparent;
}

.popper-arrow::before {
  content: '';
  position: absolute;
  border: 6px solid transparent;
  border-top-color: #333;
  top: -7px;
  left: -6px;
}

.popper-content {
  line-height: 1.4;
}

.popper-content :deep(p) {
  margin: 0 0 8px 0;
}

.popper-content :deep(p:last-child) {
  margin-bottom: 0;
}
</style>
